import React, { useEffect, useState } from "react";
import QRCode from "qrcode.react";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import {
  PhoneAndroid,
  MoreVert,
  CropFree,
} from "@material-ui/icons";
import api from "../../services/api";
import { socketConnection } from "../../services/socket";

const useStyles = makeStyles(theme => ({
  "@keyframes scanMove": {
    "0%":   { top: 0,    opacity: 1 },
    "95%":  {             opacity: 1 },
    "100%": { top: "100%", opacity: 0 },
  },
  "@keyframes cornerBlink": {
    "0%, 100%": { opacity: 1 },
    "50%":      { opacity: 0.2 },
  },
  "@keyframes dots": {
    "0%, 80%, 100%": { opacity: 0 },
    "40%":           { opacity: 1 },
  },

  title: {
    textAlign: "center",
    paddingBottom: 0,
    "& h2": { fontWeight: 700 },
  },

  content: {
    padding: theme.spacing(2, 3, 3),
  },

  /* ---------- instructions ---------- */
  step: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2.5),
  },
  stepLeft: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  stepNumber: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    fontWeight: 700,
  },
  stepIcon: {
    color: theme.palette.primary.light,
    fontSize: 18,
  },
  stepTitle: {
    fontWeight: 600,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    lineHeight: 1.35,
  },
  stepDesc: {
    fontSize: "0.76rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
    lineHeight: 1.4,
  },

  /* ---------- QR panel ---------- */
  qrPanel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderLeft: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      borderLeft: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
      paddingLeft: 0,
      paddingTop: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
  },

  /* frame ao redor do QR */
  qrFrame: {
    position: "relative",
    // Fundo sempre branco, independente do tema: o QR Code precisa de uma
    // "quiet zone" clara ao redor para os leitores (câmera do celular)
    // conseguirem localizar o padrão. No tema dark, o fundo escuro do
    // modal encostado direto no QR atrapalhava a leitura.
    padding: 16,
    borderRadius: 10,
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
    lineHeight: 0,
  },
  scanLine: {
    position: "absolute",
    left: 16,
    right: 16,
    height: 2,
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
    animation: "$scanMove 2.2s linear infinite",
    pointerEvents: "none",
    zIndex: 2,
  },

  /* placeholder enquanto carrega */
  placeholder: {
    width: 256,
    height: 256,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.03)",
  },
  corner: {
    position: "absolute",
    width: 28,
    height: 28,
    animation: "$cornerBlink 1.6s ease-in-out infinite",
  },
  cTL: {
    top: 12, left: 12,
    borderTop:  `3px solid ${theme.palette.primary.main}`,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    borderRadius: "3px 0 0 0",
    animationDelay: "0s",
  },
  cTR: {
    top: 12, right: 12,
    borderTop:   `3px solid ${theme.palette.primary.main}`,
    borderRight: `3px solid ${theme.palette.primary.main}`,
    borderRadius: "0 3px 0 0",
    animationDelay: "0.4s",
  },
  cBL: {
    bottom: 12, left: 12,
    borderBottom: `3px solid ${theme.palette.primary.main}`,
    borderLeft:   `3px solid ${theme.palette.primary.main}`,
    borderRadius: "0 0 0 3px",
    animationDelay: "0.8s",
  },
  cBR: {
    bottom: 12, right: 12,
    borderBottom: `3px solid ${theme.palette.primary.main}`,
    borderRight:  `3px solid ${theme.palette.primary.main}`,
    borderRadius: "0 0 3px 0",
    animationDelay: "1.2s",
  },

  placeholderText: {
    marginTop: theme.spacing(1.5),
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  dotsWrap: {
    display: "inline-flex",
    gap: 3,
    "& span": {
      width: 4,
      height: 4,
      borderRadius: "50%",
      backgroundColor: theme.palette.text.secondary,
      animation: "$dots 1.4s ease-in-out infinite",
      "&:nth-child(2)": { animationDelay: "0.2s" },
      "&:nth-child(3)": { animationDelay: "0.4s" },
    },
  },

  qrHint: {
    marginTop: theme.spacing(1),
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
}));

const Step = ({ number, icon: Icon, title, desc, classes }) => (
  <div className={classes.step}>
    <div className={classes.stepLeft}>
      <div className={classes.stepNumber}>{number}</div>
      <Icon className={classes.stepIcon} />
    </div>
    <div>
      <Typography className={classes.stepTitle}>{title}</Typography>
      <Typography className={classes.stepDesc}>{desc}</Typography>
    </div>
  </div>
);

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");

  useEffect(() => {
    if (!open) { setQrCode(""); return; }
    const fetchSession = async () => {
      if (!whatsAppId) return;
      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId, open]);

  useEffect(() => {
    if (!open || !whatsAppId) return;
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-whatsappSession`, data => {
      // Antes qualquer conexão sem QR fechava o modal, inclusive o próprio
      // "OPENING" de uma conexão recém-criada, antes de o QR ser gerado.
      if (data.action !== "update" || data.session.id !== whatsAppId) return;
      const { status, qrcode } = data.session;

      if (qrcode) {
        setQrCode(qrcode);
        return;
      }
      // Iniciando ou reconectando logo após a leitura: aguarda o desfecho.
      if (status === "OPENING" || status === "PENDING") {
        setQrCode("");
        return;
      }
      if (status === "CONNECTED") {
        toast.success(
          "WhatsApp conectado! 🎉 Para testar, mande um \"oi\" de outro celular para este número e acompanhe em Atendimentos.",
          { autoClose: 10000 }
        );
      }
      onClose();
    });

    return () => socket.disconnect();
  }, [open, whatsAppId, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle className={classes.title}>
        Conectar WhatsApp
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Grid container>
          {/* Instruções */}
          <Grid item xs={12} sm={6}>
            <Step
              number="1"
              icon={PhoneAndroid}
              title="Abra o WhatsApp"
              desc="No seu celular, certifique-se de estar conectado à internet."
              classes={classes}
            />
            <Step
              number="2"
              icon={MoreVert}
              title='Toque em ⋮ → "Aparelhos Conectados"'
              desc='Também pode aparecer como "Dispositivos vinculados".'
              classes={classes}
            />
            <Step
              number="3"
              icon={CropFree}
              title="Aponte para o QR Code"
              desc="Mantenha a tela iluminada para uma leitura mais rápida."
              classes={classes}
            />
          </Grid>

          {/* QR Code */}
          <Grid item xs={12} sm={6} className={classes.qrPanel}>
            {qrCode ? (
              <>
                <div className={classes.qrFrame}>
                  <div className={classes.scanLine} />
                  <QRCode value={qrCode} size={248} level="H" includeMargin />
                </div>
                <Typography className={classes.qrHint}>
                  Aponte a câmera para conectar
                </Typography>
              </>
            ) : (
              <>
                <div className={classes.placeholder}>
                  <div className={`${classes.corner} ${classes.cTL}`} />
                  <div className={`${classes.corner} ${classes.cTR}`} />
                  <div className={`${classes.corner} ${classes.cBL}`} />
                  <div className={`${classes.corner} ${classes.cBR}`} />
                  <CircularProgress size={44} />
                </div>
                <div className={classes.placeholderText}>
                  Gerando QR Code
                  <span className={classes.dotsWrap}>
                    <span /><span /><span />
                  </span>
                </div>
              </>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
